import { useState, useEffect } from 'react';
import { Bell, CheckCircle, TrendingUp, Target, Trophy, Check, Mail, Trash2 } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAuth } from '@/hooks/useAuth';
import { useNotifications } from '@/hooks/useNotifications';
import {
  getNotifications,
  markNotificationAsRead,
  markNotificationAsUnread,
  markAllNotificationsAsRead,
  deleteNotification,
} from '@/services/api/notifications.api';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { Badge } from '@/components/atoms/Badge';
import type { Notification } from '@/types/gamification.types';
import styles from './NotificationsPage.module.scss';

// TODO Phase 5+: Implement NotificationContext, API integration

function getNotificationIcon(type: Notification['type']) {
  switch (type) {
    case 'achievement_unlocked':
      return Trophy;
    case 'level_up':
    case 'rank_change':
      return TrendingUp;
    case 'streak_milestone':
      return CheckCircle;
    case 'story_assigned':
    case 'story_reminder':
      return Target;
    case 'system':
    default:
      return Bell;
  }
}

function getRelativeTime(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
}

export function NotificationsPage() {
  const { user } = useAuth();
  const { refreshCount } = useNotifications();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadNotifications = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await getNotifications(user.id, { limit: 20 });
        setNotifications(data);
      } catch (error) {
        console.error('Failed to load notifications:', error);
      } finally {
        setIsLoading(false);
      }
    };

    void loadNotifications();
  }, [user?.id]);

  const handleMarkAsRead = async (id: string) => {
    try {
      await markNotificationAsRead(id);
      setNotifications((prev) =>
        prev.map((notif) => (notif.id === id ? { ...notif, isRead: true } : notif))
      );
      void refreshCount();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAsUnread = async (id: string) => {
    try {
      await markNotificationAsUnread(id);
      setNotifications((prev) =>
        prev.map((notif) => (notif.id === id ? { ...notif, isRead: false } : notif))
      );
      void refreshCount();
    } catch (error) {
      console.error('Failed to mark notification as unread:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteNotification(id);
      setNotifications((prev) => prev.filter((notif) => notif.id !== id));
      void refreshCount();
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.id) return;
    try {
      await markAllNotificationsAsRead(user.id);
      setNotifications((prev) => prev.map((notif) => ({ ...notif, isRead: true })));
      void refreshCount();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <div className={styles.notifications}>
      <div className={styles.notifications__header}>
        <div>
          <h1 className={styles.notifications__title}>
            Notifications
            {unreadCount > 0 && (
              <Badge variant="secondary" className={styles.notifications__badge}>
                {unreadCount}
              </Badge>
            )}
          </h1>
          <p className={styles.notifications__subtitle}>Stay updated with your team's progress and activity</p>
        </div>

        {notifications.length > 0 && (
          <div className={styles.notifications__actions}>
            {unreadCount > 0 && (
              <Button variant="outline" size="sm" onClick={handleMarkAllAsRead}>
                Mark all as read
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={handleMarkAllAsRead}>
              Clear all
            </Button>
          </div>
        )}
      </div>

      <div className={styles.notifications__list}>
        {isLoading ? (
          <div className={styles.notifications__loading}>Loading notifications...</div>
        ) : notifications.length === 0 ? (
          <div className={styles.notifications__empty}>
            <Icon icon={Bell} size="lg" />
            <p className={styles.notifications__emptyText}>No notifications yet</p>
            <p className={styles.notifications__emptySubtext}>
              We'll notify you when there's something new with your team
            </p>
          </div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              className={cn(styles.notifications__item, !notification.isRead && styles['notifications__item--unread'])}
            >
              <div className={styles.notifications__itemIcon}>
                <Icon icon={getNotificationIcon(notification.type)} size="md" />
              </div>

              <div className={styles.notifications__itemContent}>
                <h3 className={styles.notifications__itemTitle}>{notification.title}</h3>
                <p className={styles.notifications__itemMessage}>{notification.message}</p>
                <p className={styles.notifications__itemTimestamp}>
                  {getRelativeTime(notification.createdAt)}
                </p>
              </div>

              <div className={styles.notifications__itemActions}>
                {notification.isRead ? (
                  <button
                    className={styles.notifications__actionButton}
                    onClick={() => handleMarkAsUnread(notification.id)}
                    title="Mark as unread"
                  >
                    <Icon icon={Check} size="sm" />
                  </button>
                ) : (
                  <button
                    className={styles.notifications__actionButton}
                    onClick={() => handleMarkAsRead(notification.id)}
                    title="Mark as read"
                  >
                    <Icon icon={Mail} size="sm" />
                  </button>
                )}
                <button
                  className={cn(styles.notifications__actionButton, styles['notifications__actionButton--danger'])}
                  onClick={() => handleDelete(notification.id)}
                  title="Delete"
                >
                  <Icon icon={Trash2} size="sm" />
                </button>
              </div>

              {!notification.isRead && <div className={styles.notifications__itemDot} />}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default NotificationsPage;
